import { apiClient } from './client'

export interface BulkImportResponse {
  job_id: string
  batch_id: string
  status: string
}

export interface BulkResumeResponse {
  success?: boolean
  message?: string
  job_id?: string | null
  status: string
  batch_id?: string | null
  batch_status?: string | null
  resuming_from_section?: number | null
}

export interface BulkSkipSectionResponse {
  session_id: string
  fallback_file_id: string
}

export interface BulkFallbackRetryResponse {
  job_id: string
  session_id: string
}

export interface BulkCleanupResponse {
  cleaned_count: number
  failed_count: number
  total_stale: number
}

export interface ShopItem {
  token: string
  field: string
  display: string
  pages?: number[]
  suggested_id?: string | null
  suggested_ids?: string[]
  occupied?: Record<string, string>
}

export interface CatalogTarget {
  id: string
  name: string
  code?: string | null
  aliases?: string[]
}

export interface DetectResponse {
  file_id: string
  page_count: number
  is_multi_page: boolean
  use_bulk_import: boolean
}

export interface BulkJobStatusResponse {
  job_id: string
  status: string
  progress: number
  message: string
  result: Record<string, unknown> | null
  error: string | null
  started_at: string | null
  completed_at: string | null
  batch_id: string | null
  batch_status: string | null
  paused_at_section: number | null
}

export const bulkTimetableImportApi = {
  detect(fileId: string): Promise<DetectResponse> {
    return apiClient.get('/api/v1/timetables/bulk/detect', {
      params: { file_id: fileId },
    }).then(r => r.data)
  },

  startImport(data: { uploaded_file_id: string; template_id?: string; max_retries?: number; force_restart?: boolean; valid_from?: string | null; valid_to?: string | null }): Promise<BulkImportResponse> {
    return apiClient.post('/api/v1/timetables/bulk/import', data, { timeout: 300000 }).then(r => r.data)
  },

  getJobStatus(jobId: string): Promise<BulkJobStatusResponse> {
    return apiClient.get(`/api/v1/timetables/bulk/jobs/${jobId}/status`).then(r => r.data)
  },

  async createJobEventSource(jobId: string): Promise<EventSource> {
    const data = await apiClient
      .post<{ ticket: string }>(`/api/v1/timetables/bulk/jobs/${jobId}/stream-ticket`)
      .then((response) => response.data)
    const baseUrl = apiClient.defaults?.baseURL || ''
    const url = `${baseUrl}/api/v1/timetables/bulk/jobs/${jobId}/stream?ticket=${encodeURIComponent(data.ticket)}`
    return new EventSource(url)
  },

  resumeBatch(batchId: string): Promise<BulkResumeResponse> {
    return apiClient.post(`/api/v1/timetables/bulk/batches/${batchId}/resume`, null, { timeout: 300000 }).then(r => r.data)
  },

  skipSection(batchId: string, sessionId: string): Promise<BulkSkipSectionResponse> {
    return apiClient.post(`/api/v1/timetables/bulk/batches/${batchId}/skip-section`, {
      session_id: sessionId,
    }).then(r => r.data)
  },

  retryFallback(fallbackFileId: string): Promise<BulkFallbackRetryResponse> {
    return apiClient.post(`/api/v1/timetables/bulk/fallback/${fallbackFileId}/retry`).then(r => r.data)
  },

  cleanupBatch(batchId: string, abandon = false): Promise<{ batch_id: string; status: string }> {
    return apiClient.delete(`/api/v1/timetables/bulk/batches/${batchId}/cleanup`, {
      params: abandon ? { abandon: true } : undefined,
    }).then(r => r.data)
  },

  cleanupStale(hoursThreshold = 24): Promise<BulkCleanupResponse> {
    return apiClient.post('/api/v1/timetables/bulk/cleanup-stale', { hours_threshold: hoursThreshold }).then(r => r.data)
  },

  cleanupOldBatches(): Promise<{ cleaned_count: number; message: string }> {
    return apiClient.delete('/api/v1/timetables/bulk/batches/cleanup-old').then(r => r.data)
  },

  getTemplateForFile(fileId: string): Promise<{ template_id: string | null; template_name: string | null; confirmed_token_count: number }> {
    return apiClient.get(`/api/v1/timetables/bulk/files/${fileId}/template`).then(r => r.data)
  },

  getShopItems(batchId: string): Promise<{
    items: ShopItem[]
    batch_status: string
    file_id: string | null
    targets?: Record<string, CatalogTarget[]>
    can_undo?: boolean
    ignored_tokens?: string[]
    kept_whole?: string[]
  }> {
    return apiClient.get(`/api/v1/timetables/bulk/batches/${batchId}/shop-items`).then(r => r.data)
  },

  resolveShopItems(batchId: string, items: Array<{
    token: string
    field: string
    source_field?: string
    canonical_id?: string
    action?: string
    name?: string
    parts?: { token: string; field: string }[]
    background_ids?: string[]
  }>, signal?: AbortSignal): Promise<{ updated: number; remaining: ShopItem[]; targets?: Record<string, CatalogTarget[]>; can_undo?: boolean; item_index?: number; ignored_tokens?: string[]; closed?: boolean }> {
    return apiClient.post(`/api/v1/timetables/bulk/batches/${batchId}/shop-resolve-batch`, { items }, { signal, timeout: 300000 }).then(r => r.data)
  },

  resolveShopItem(batchId: string, data: {
    token: string
    field: string
    source_field?: string
    canonical_id?: string
    action?: string
    name?: string
    parts?: { token: string; field: string }[]
    background_ids?: string[]
  }, signal?: AbortSignal): Promise<{ updated: number; remaining: ShopItem[]; targets?: Record<string, CatalogTarget[]>; can_undo?: boolean; item_index?: number; ignored_tokens?: string[]; closed?: boolean }> {
    return apiClient.post(`/api/v1/timetables/bulk/batches/${batchId}/shop-resolve`, data, { signal }).then(r => r.data)
  },

  undoShopItem(batchId: string): Promise<{
    remaining: ShopItem[]
    item_index: number
    can_undo: boolean
    targets?: Record<string, CatalogTarget[]>
    token?: string
    field?: string
    ignored_tokens?: string[]
    kept_whole?: string[]
  }> {
    return apiClient.post(`/api/v1/timetables/bulk/batches/${batchId}/shop-undo`).then(r => r.data)
  },
}
